import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRightLeft, Scan, Check, Package, Trash2, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import * as greigeStorageApi from '@/services/greigeStorageApi';
import * as validationService from '@/services/validationService';
import { DestinationScanCard } from '@/components/greigeStorage/DestinationScanCard';
import { LockedTransferNote } from '@/components/wms/LockedTransferNote';

const LocationTransfer = () => {
  const navigate = useNavigate();
  const [staffId, setStaffId] = useState('');
  const [staffVerified, setStaffVerified] = useState(false);
  const [staffName, setStaffName] = useState('');
  
  const [dest, setDest] = useState(null); // {location_id, footprint, occupant_design}

  const [bundleBarcode, setBundleBarcode] = useState('');
  const [scannedBundles, setScannedBundles] = useState([]); // [{barcode, footprint, sort_no}]
  const [scanning, setScanning] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const barcodeRef = useRef(null);

  useEffect(() => { if (dest) barcodeRef.current?.focus(); }, [dest]);

  const handleDestChange = (next) => {
    if (!next && scannedBundles.length > 0) toast.info('Destination cleared. Scan the new destination, then the bundles again.');
    if (!next) setScannedBundles([]);
    setDest(next);
  };

  const handleVerifyStaff = async () => {
    if (!staffId.trim()) {
      toast.error('Please enter Staff ID');
      return;
    }
    try {
      const result = await validationService.quickValidateStaff(staffId.trim());
      if (result.valid) {
        setStaffVerified(true);
        setStaffName(result.staffName);
        toast.success(`Staff verified: ${result.staffName}`);
      } else {
        toast.error(result.message || 'Staff not found');
      }
    } catch (error) {
      toast.error('Staff not found');
    }
  };

  const handleScanBundle = async (e) => {
    if (e.key !== 'Enter') return;
    const code = bundleBarcode.trim();
    if (!code) return;
    if (!dest) {
      toast.error('Scan the destination location first');
      setBundleBarcode('');
      return;
    }

    if (scannedBundles.some(b => b.barcode === code)) {
      toast.error('Bundle already scanned');
      setBundleBarcode('');
      return;
    }

    try {
      setScanning(true);
      const info = await greigeStorageApi.getStoredBundle(code);
      if (!info.found) {
        toast.error(`${code}: ${info.reason || 'not currently in storage'}`);
        setBundleBarcode('');
        return;
      }
      if (info.location_id === dest.location_id) {
        toast.warning(`${code} is already in the destination location`);
        setBundleBarcode('');
        return;
      }
      if (dest.occupant_design && info.design_name && info.design_name !== dest.occupant_design) {
        toast.error(`${code} is design '${info.design_name}'. ${dest.footprint} holds '${dest.occupant_design}'. One design per bin.`);
        setBundleBarcode('');
        return;
      }
      const existingDesign = scannedBundles.find(b => b.design_name)?.design_name;
      if (existingDesign && info.design_name && info.design_name !== existingDesign) {
        toast.error(`${code} is design '${info.design_name}' — you're already moving '${existingDesign}'. One design per bin.`);
        setBundleBarcode('');
        return;
      }
      setScannedBundles(prev => [
        { barcode: code, footprint: info.footprint, sort_no: info.sort_no, design_name: info.design_name, source_lock: info.source_lock || null },
        ...prev,
      ]);
      toast.success(`Added — ${info.design_name || 'bundle'} @ ${info.footprint || 'unknown location'}`);
      setBundleBarcode('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to look up bundle');
    } finally {
      setScanning(false);
      barcodeRef.current?.focus();
    }
  };

  const handleRemoveBundle = (barcode) => {
    setScannedBundles(prev => prev.filter(b => b.barcode !== barcode));
  };

  const handleSubmit = async () => {
    if (!dest) {
      toast.error('Please scan a destination location');
      return;
    }
    if (scannedBundles.length === 0) {
      toast.error('Please scan at least one bundle');
      return;
    }

    try {
      setSubmitting(true);
      const result = await greigeStorageApi.locationTransfer(
        staffId,
        dest.location_id,
        scannedBundles.map(b => b.barcode)
      );

      toast.success(`Transferred ${result.transferred?.length || 0} bundles to ${dest.footprint}`);

      if (result.errors?.length > 0) {
        result.errors.forEach(err => toast.warning(err));
      }

      setScannedBundles([]);
      setDest(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transfer failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/sections/greige-storage')} 
              className="text-[#F9FAFB] hover:bg-white/10"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Location to Location Transfer</h1>
              <p className="text-sm text-[#E8DCC8]">Greige Storage</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 md:px-6 py-8">
        {/* Staff Verification */}
        {!staffVerified ? (
          <Card className="border-[#E5DFD0]">
            <CardHeader>
              <CardTitle>Staff Verification</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="staffId">Staff ID</Label>
                  <Input
                    id="staffId"
                    value={staffId}
                    onChange={(e) => setStaffId(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleVerifyStaff()}
                    placeholder="Scan or enter Staff ID..."
                    className="mt-1"
                    data-testid="lt-staff-input"
                  />
                </div>
                <Button onClick={handleVerifyStaff} className="w-full bg-[#2D5F4F] hover:bg-[#1A1F2E]" data-testid="lt-verify-staff-btn">
                  Verify Staff
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Staff Info */}
            <Card className="border-[#E5DFD0] mb-6">
              <CardContent className="py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                      <Check className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-[#5A5F6E]">Staff</p>
                      <p className="font-medium text-[#1A1F2E]">{staffName} ({staffId})</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Destination Location — scanned, not selected */}
            <DestinationScanCard dest={dest} onChange={handleDestChange} />

            {/* Bundle Scanning */}
            <Card className="border-[#E5DFD0] mb-6">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Scan className="w-5 h-5" />
                    Scan Bundles
                  </span>
                  <Badge>{scannedBundles.length} Scanned</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Input
                  ref={barcodeRef}
                  value={bundleBarcode}
                  onChange={(e) => setBundleBarcode(e.target.value)}
                  onKeyDown={handleScanBundle}
                  placeholder={!dest ? 'Scan the destination location first' : (scanning ? 'Looking up bundle…' : 'Scan bundle barcode...')}
                  disabled={scanning || !dest}
                  data-testid="bundle-barcode-input"
                />

                {scannedBundles.length > 0 && (
                  <div className="mt-4 space-y-2 max-h-72 overflow-y-auto">
                    {scannedBundles.map((b, idx) => (
                      <div key={b.barcode} className="flex items-center justify-between p-2 bg-gray-50 rounded"
                        data-testid={`scanned-bundle-${idx}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-xs text-gray-500">{idx + 1}</span>
                          <Package className="w-4 h-4 text-gray-400 shrink-0" />
                          <div className="min-w-0">
                            <span className="font-mono text-sm block truncate">{b.barcode}</span>
                            <span className="text-xs text-[#5A5F6E] flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {b.footprint || 'Unknown location'}
                              <LockedTransferNote lock={b.source_lock} compact testId={`scanned-lock-${idx}`} />
                            </span>
                            {b.design_name && (
                              <span className="text-xs font-medium text-[#2D5F4F] truncate block" data-testid={`scanned-design-${idx}`}>
                                {b.design_name}
                              </span>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveBundle(b.barcode)}
                          className="h-6 w-6 p-0 text-red-500 hover:text-red-700 shrink-0"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Submit */}
            <Button 
              onClick={handleSubmit}
              disabled={submitting || !dest || scannedBundles.length === 0}
              className="w-full bg-[#2D5F4F] hover:bg-[#1A1F2E]"
              data-testid="submit-transfer-btn"
            >
              <ArrowRightLeft className="w-4 h-4 mr-2" />
              {submitting ? 'Transferring...' : `Transfer ${scannedBundles.length} Bundles`}
            </Button>
          </>
        )}
      </main>
    </div>
  );
};

export default LocationTransfer;
